import Icon from '@atoms/Icon/Icon';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IBreadcrumb } from '@models/IBreadcrumb';
import useProductPageContentStore from '@state/ProductPageStore';
import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';

const Breadcrumb = (props: IBreadcrumb) => {
  const { breadcrumbAriaLabel, breadcrumbFooterAriaLabel } = useAriaLabels();
  const isMobile = useMediaQuery(768);

  const {
    links,
    activePage,
    isInFooter,
    isOnProductOverviewPage,
    shouldHavePadding,
  } = useProductPageContentStore().productPageContent?.breadcrumbs || props;

  return (
    <section
      className={classNames('relative w-full', {
        'bg-white dark:bg-black-100': !isInFooter,
        'lg:py-6': !isInFooter && !isOnProductOverviewPage,
        'mb-7.5 lg:mb-24': shouldHavePadding,
      })}
      data-testid="CT-31-Breadcrumbs"
    >
      <div className={classNames({ 'md:container': !isInFooter })}>
        <nav
          className={classNames('relative flex w-full', {
            'px-5 md:px-0': !isInFooter,
          })}
          data-testid="CT-31-Breadcrumbs-Nav"
          aria-label={
            isInFooter ? breadcrumbFooterAriaLabel : breadcrumbAriaLabel
          }
        >
          <ShadowContainerHorizontal
            shadowColour={classNames(
              'dark:from-grey-600',
              isInFooter ? 'from-grey-100' : 'from-white'
            )}
            shadowWidth="w-13"
            className={isOnProductOverviewPage ? 'py-3' : 'py-4'}
            threshold={0.01}
            hasTabbableNestedItems
          >
            <ul
              className="flex flex-wrap gap-y-4"
              itemScope
              itemType="https://schema.org/BreadcrumbList"
            >
              {links?.length > 0 &&
                links.map((link, index) => (
                  <li
                    className="flex flex-row items-center"
                    key={link.name}
                    itemProp="itemListElement"
                    itemScope
                    itemType="https://schema.org/ListItem"
                  >
                    {link.name && link.url && (
                      <>
                        <a
                          className="body-two text-black-100 hover:underline dark:text-white"
                          href={link.url}
                          onClick={() =>
                            updateDataLayer({
                              event: 'breadcrumbs',
                              content_type: link.name,
                            })
                          }
                          data-testid={`CT-31-Breadcrumbs-Link-Item-${index}`}
                          itemProp="item"
                        >
                          <span itemProp="name">
                            {link.name.length > 15 && isMobile
                              ? `${link.name.substring(0, 15)}...`
                              : link.name}
                          </span>
                        </a>
                        <meta itemProp="position" content={`${index + 1}`} />

                        <Icon
                          name="chevron_right"
                          colorClassName="dark:text-white"
                          className="lg:mx-1"
                        />
                      </>
                    )}
                  </li>
                ))}

              {activePage?.name && (
                <li
                  className="body-two text-grey-450 dark:text-grey-500"
                  data-testid="CT-31-Breadcrumbs-Active-Page-Item"
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem"
                >
                  <span itemProp="name">
                    {activePage.name.length > 15 && isMobile
                      ? `${activePage.name.substring(0, 15)}...`
                      : activePage.name}
                  </span>

                  <meta
                    itemProp="position"
                    content={`${(links?.length || 0) + 1}`}
                  />
                </li>
              )}
            </ul>
          </ShadowContainerHorizontal>
        </nav>
      </div>
    </section>
  );
};

export default Breadcrumb;
